import React from "react";
import SignaturePad from "@/components/SignaturePad";
import DowAuth, { validateDowAuth } from "./dow/DowAuth";
import { Dow } from "./dow/shared";

export type Consent = {
  agreed?: boolean; release_agreed?: boolean; signature_name?: string; signature_image?: string | null;
  arrests?: string; convictions?: string; criminal_explanation?: string;
  payment_agreed?: string; payment_signature_name?: string; payment_signature_image?: string | null;
  state_auth_agreed?: boolean; state_auth_signature_name?: string; state_auth_signature_image?: string | null;
};

export const STATE_AUTH_TITLE = "Authorization to Conduct Criminal Background Search / Investigation";
export const STATE_AUTH_PARAS = [
  "I hereby authorize Base Ent Inc. d/b/a Info Retrieval Services (\"Info Retrieval\"), 212 S Bradford Street, Seaford, DE 19973, and its authorized agents, to conduct a criminal background search and investigation about me in connection with my application with the organization named below.",
  "This authorization includes searches of criminal court records, state criminal history repositories, law enforcement records, sex offender registries and other public records in any state, county or other jurisdiction where I live or have lived, including any state or jurisdiction that requires an individual's written authorization or signed consent before such records may be searched or released.",
  "I authorize any court, government agency, state repository, law enforcement agency or other record custodian to release this information to Info Retrieval or its authorized agents. I release Info Retrieval, its agents and any person or agency furnishing information from any claim arising from the lawful release or use of this information for this purpose.",
  "I agree that a copy or electronic version of this authorization is as valid as the original, and that my electronic signature below is my legal signature with the same force and effect as a handwritten signature. This authorization is valid for the purpose of this application.",
];

export function validateConsent(c: Consent, w: Dow, foreign: boolean, noCourt = false, stateAuth = false): string | null {
  if (!noCourt && (!c.arrests || !c.convictions)) return "Please answer both court questions (arrests and convictions).";
  if (!noCourt && (c.arrests === "yes" || c.convictions === "yes") && !(c.criminal_explanation || "").trim()) return "Please explain your 'Yes' answer in the court information box.";
  const err = validateDowAuth(c, w, foreign);
  if (err) return err;
  if (stateAuth && !c.state_auth_agreed) return "Please read and agree to the State Authorization.";
  if (stateAuth && (!c.state_auth_signature_name || c.state_auth_signature_name.trim().length < 3)) return "Please type your full name to sign the State Authorization.";
  if (stateAuth && !c.state_auth_signature_image) return "Please draw your signature on the State Authorization.";
  return null;
}

export function YesNo({ label, hint, value, onChange, testId, disabled = false }: { label: string; hint: string; value?: string; onChange: (v: string) => void; testId: string; disabled?: boolean }) {
  return (
    <div>
      <span className="field-label">{label}</span>
      <div className="flex gap-3">
        {["yes", "no"].map((v) => (
          <button key={v} type="button" data-testid={`${testId}-${v}`} disabled={disabled} onClick={() => onChange(v)}
            className={`h-12 min-w-[96px] rounded-xl border-2 px-5 text-base font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${value === v ? "border-[#1B365D] bg-[#1B365D] text-white" : "border-slate-200 bg-white text-[#1A2536] hover:border-[#1B365D]/40"}`}>
            {v === "yes" ? "Yes" : "No"}
          </button>
        ))}
      </div>
      <p className="field-hint italic">{hint}</p>
    </div>
  );
}

function Agree({ checked, onChange, text, testId }: { checked?: boolean; onChange: (v: boolean) => void; text: string; testId: string }) {
  return (
    <label className="flex cursor-pointer items-start gap-4 rounded-2xl border-2 border-slate-200 bg-white p-5 transition-colors hover:border-[#1B365D]/40">
      <input type="checkbox" data-testid={testId} className="mt-1 h-6 w-6 shrink-0 accent-[#1B365D]" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-base font-medium text-[#1A2536]">{text}</span>
    </label>
  );
}

type Props = { c: Consent; w: Dow; foreign: boolean; orgName?: string; setC: (k: keyof Consent) => (v: any) => void; setW: (k: keyof Dow) => (v: any) => void; noCourt?: boolean; stateAuth?: boolean };

export default function StepConsent({ c, w, foreign, setC: set, setW, noCourt = false, stateAuth = false, orgName }: Props) {
  return (
    <div className="space-y-6">
      {!noCourt && <div className="card-soft space-y-5 p-6" data-testid="court-info-section">
        <h3 className="text-lg font-bold text-[#1A2536]">Court information</h3>
        <p className="font-semibold text-[#1A2536]">Please note: criminal cases or convictions do NOT automatically disqualify you.</p>
        <YesNo label="Arrests" hint='Choose "Yes" if you have been arrested or charged with a crime or DUI in the past 7 years.' value={c.arrests} onChange={set("arrests")} testId="court-arrests" />
        <YesNo label="Convictions" hint='Choose "Yes" if you have EVER been CONVICTED of a crime or DUI.' value={c.convictions} onChange={set("convictions")} testId="court-convictions" />
        <label className="block">
          <span className="field-label">Explanation {(c.arrests === "yes" || c.convictions === "yes") ? "(required)" : "(if you answered Yes)"}</span>
          <textarea rows={3} data-testid="court-explanation-input" className="field-input h-auto py-3" value={c.criminal_explanation || ""} onChange={(e) => set("criminal_explanation")(e.target.value)} />
        </label>
      </div>}

      <DowAuth c={c} w={w} foreign={foreign} setC={set} set={setW} org={orgName || "the organization"} />
      {stateAuth && (
        <div className="space-y-4 border-t border-slate-200 pt-6" data-testid="state-auth-section">
          <div className="card-soft max-h-80 overflow-y-auto p-6 text-[15px] leading-relaxed text-[#334155]" data-testid="state-auth-text">
            <h3 className="mb-3 text-lg font-bold text-[#1A2536]">{STATE_AUTH_TITLE}</h3>
            {STATE_AUTH_PARAS.map((t) => <p key={t.slice(0, 20)} className="mb-3">{t}</p>)}
            {orgName && <p className="text-sm"><b>Organization:</b> {orgName}</p>}
          </div>
          <Agree checked={c.state_auth_agreed} onChange={set("state_auth_agreed")} testId="state-auth-checkbox" text="Yes, I have read and agree to this State Authorization." />
          <label className="block">
            <span className="field-label">Type your full name to sign the State Authorization</span>
            <input data-testid="state-auth-signature-name-input" className="field-input font-heading text-lg italic" value={c.state_auth_signature_name || ""} onChange={(e) => set("state_auth_signature_name")(e.target.value)} />
          </label>
          <div data-testid="state-auth-signature-pad"><SignaturePad value={c.state_auth_signature_image} onChange={(img) => set("state_auth_signature_image")(img)} /></div>
        </div>
      )}
    </div>
  );
}
